import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../CDN';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import FAQWidget from '../widgets/interactive/FAQWidget';

type FreshserviceLandingPageStaticProps = {};

export const getStaticProps: GetStaticProps<FreshserviceLandingPageStaticProps> = async (context) => {
  const props: FreshserviceLandingPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const FreshserviceLandingPage: NextPageWithLayout<FreshserviceLandingPageStaticProps> = (props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.LandingPageFreshservice);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="FreshService Integration"
        title="Gaspar AI & Fresh-service integration"
        description="With our Freshservice integration, you bring Generative AI to your favorite tools, enrich them with automation capabilities and maximize their use and ROI. Gaspar AI automatically and instantly resolves 40% of employee issues, deflecting tickets and reducing manual work."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Main Integration Features" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-automatically-resolve-issues'),
              orientation: 'left',
              title: 'Automatically resolve issues & automate approvals on Slack and MS Teams',
              description:
                'Gaspar AI’s Generative AI chatbot automatically and instantly resolves 40% of employee issues without human intervention, on Slack and MS Teams, powered by LLMs and 3rd party plugins. This dramatically reduces helpdesk ticket costs and volume.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-offer-accurate-human-like-answers'),
              orientation: 'right',
              title: 'Offer accurate, human-like answers to employee questions',
              description:
                'Your workforce can instantly find information - HR policies, IT troubleshooting and more just by asking Gaspar, our AI Virtual Agent. It searches articles, documents and data across knowledge bases and repositories, and can ask follow-up questions until employees get the exact answer.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-automate-ticket-creation'),
              orientation: 'left',
              title: 'Automate ticket creation on Freshservice',
              description:
                "In case of auto-resolution, a ticket is automatically created on your ITSM platform. If the chatbot can't resolve the issue, it facilitates handoff to the right agent with easy ticket creation and smart routing.",
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-optimise-your-knowledge-base'),
              orientation: 'right',
              title: 'Optimize your knowledge base',
              description:
                'All content built for your company’s knowledge base (be it Coda, Google docs, Sharepoint, etc.) is leveraged by Gaspar to answer questions. Moreover, Gaspar AI helps you identify article gaps with AI recommendations based on real employee demand and create related content.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-automate-repetitive-processes'),
              orientation: 'left',
              title: 'Automate repetitive processes',
              description:
                'Reduce manual work by 80% and minimize repetitive tasks such as onboarding by creating no-code, customized workflows. You can activate them whenever needed or set up triggers.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-get-unique-business-insights'),
              orientation: 'right',
              title: 'Get unique Business Insights',
              description:
                'Uncover the most common employee issues, what’s hurting their productivity, which automations or content will unblock them and prioritise those that will have the biggest business impact.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-measure-your-investment-impact'),
              orientation: 'left',
              title: 'Measure your investment’s impact with our Performance Metrics',
              description:
                'Monitor which features, capabilities, and resources employees find valuable, and the performance of key KPIs. Compare performance over time, make grounded decisions and take data-driven action.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-available-for-all-customers'),
              orientation: 'right',
              title: 'Available for all Freshservice customers no matter your plan',
              description:
                'Gaspar AI’s automation capabilities are available across all Atlassian plans - free, standard, premium, enterprise with no usage limits.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-freshservice-works-on-cloud-or-infrastructure'),
              orientation: 'left',
              title: "Integration that works on Cloud or customer's infrastructure",
              description: 'Whether you host Freshservice in the cloud or on your infrastructure, Gaspar AI integrates with them anyway.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
FreshserviceLandingPage.displayName = 'FreshserviceLandingPage';
export default FreshserviceLandingPage;
